import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import TableModal from "../../../../components/reusableComponent/TableModal";
import CustomDate from "../../../../components/common/CustomDate";
import { HourlyVolumeLegend } from "../../../dashboard/email/salesCharts/HourlyVolumeLegend";
import {
  formatIsoForDayLabel,
  HourlyDaySeriesTooltip,
  hourlyChartLegendProps,
  hourlyChartTooltipProps,
  type HourlyTooltipConfig,
} from "../../../dashboard/email/salesCharts/HourlyDaySeriesTooltip";
import {
  useGetTicketClickToExpandQuery,
  useTicketDashboardQuery,
} from "../../../../Services/dashboard/TicketDashboard";

function parseYyyyMmDdToLocalDate(s: string): Date {
  if (!s || s.length < 10) return new Date();
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7)) - 1;
  const d = Number(s.slice(8, 10));
  return new Date(y, m, d);
}

function formatDateToYyyyMmDd(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function buildExpandViewDateRange(endDateStr: string) {
  const endDate = parseYyyyMmDdToLocalDate(endDateStr);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (30 - 1));
  return {
    startDate,
    endDate,
    key: "selection" as const,
  };
}

function normalizeDayDateToIsoKey(raw: any): string {
  if (raw == null || raw === "") return "_unknown";
  if (typeof raw === "string") {
    const t = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    if (/^\d{4}-\d{2}-\d{2}[T ]/.test(t)) return t.slice(0, 10);
    const d = new Date(t);
    if (!Number.isNaN(d.getTime())) return formatDateToYyyyMmDd(d);
    return t || "_unknown";
  }
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) return formatDateToYyyyMmDd(d);
  return String(raw);
}

const EMPTY_VOLUME_RESULTS: any[] = [];
const EMPTY_HOURLY_COUNT: Record<string, number> = {};

const VOLUME_COLOR_PALETTE = [
  "#6366f1",
  "#8b5cf6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
];

function extractVolumeResults(data: any): any[] {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.ticket_volume)) return data.ticket_volume;
  if (Array.isArray(data?.tickets_per_day_per_hour)) return data.tickets_per_day_per_hour;
  if (Array.isArray(data?.ticket_volume_per_day)) return data.ticket_volume_per_day;
  if (Array.isArray(data?.ticket_volume_by_hour)) return data.ticket_volume_by_hour;
  return [];
}

function buildVolumeHourlySeries(resultsIn: any[]) {
  if (!resultsIn?.length) {
    return { chartData: [] as any[], dateLabels: [] as string[], categories: [] as string[] };
  }

  const sortedDays = [...resultsIn].sort((a, b) =>
    normalizeDayDateToIsoKey(a.date).localeCompare(normalizeDayDateToIsoKey(b.date)),
  );
  const categories = Object.keys(sortedDays[0]?.hourly_count ?? EMPTY_HOURLY_COUNT).sort(
    (a, b) => Number(a) - Number(b),
  );
  const chartData = categories.map((hour) => {
    const dataPoint: Record<string, any> = {
      hour: `${String(hour).padStart(2, "0")}:00`,
      rawHour: hour,
    };
    sortedDays.forEach((day) => {
      const isoKey = normalizeDayDateToIsoKey(day.date);
      dataPoint[isoKey] = day.hourly_count?.[hour] ?? 0;
    });
    return dataPoint;
  });
  const dateLabels = sortedDays.map((day) => normalizeDayDateToIsoKey(day.date));
  return { chartData, dateLabels, categories };
}

// ── Column definitions ────────────────────────────────────────────────────────
const TICKET_TABLE_COLUMNS = [
  {
    label: "Ticket ID",
    render: (row: any) => row?.ticket_id ?? row?.id ?? "—",
  },
  { label: "Title", render: (row: any) => row?.title ?? row?.subject ?? "—" },
  { label: "Contact Name", render: (row: any) => row?.contact_name ?? "—" },
  { label: "Contact Email", render: (row: any) => row?.contact_email ?? "—" },
  { label: "Contact Phone", render: (row: any) => row?.contact_phone ?? "—" },
  {
    label: "Created",
    render: (row: any) => {
      const raw = row?.created;
      if (!raw) return "—";
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return "—";
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");
      return `${dd}/${mm}/${d.getFullYear()} ${hh}:${min}:${ss}`;
    },
  },
];

interface IPROPS {
  intervalData: any;
  startDate?: string;
  endDate?: string;
  teamId?: string | null;
  workflowId?: string | null;
}

const TicketVolumeChart: React.FC<IPROPS> = ({
  intervalData,
  startDate,
  endDate,
  teamId,
  workflowId,
}) => {
  const navigate = useNavigate();
  const hoveredPointRef = useRef<any>(null);
  const mainChartRef = useRef<HTMLDivElement>(null);
  const modalChartRef = useRef<HTMLDivElement>(null);

  const [fullscreenChart, setFullscreenChart] = useState<any>(null);
  const [modalDateRange, setModalDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [modalSelectedDates, setModalSelectedDates] = useState<string[]>([]);
  const [chartType, setChartType] = useState("bar");
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const [clickToExpandPage, setClickToExpandPage] = useState(1);

  useEffect(() => {
    if (fullscreenChart && endDate) {
      setModalDateRange([buildExpandViewDateRange(endDate)]);
    }
  }, [fullscreenChart, endDate]);

  const modalRangeStrings = useMemo(
    () => ({
      start: modalDateRange?.[0]?.startDate
        ? formatDateToYyyyMmDd(modalDateRange[0].startDate)
        : "",
      end: modalDateRange?.[0]?.endDate
        ? formatDateToYyyyMmDd(modalDateRange[0].endDate)
        : "",
    }),
    [modalDateRange],
  );

  const { data: modalOverviewData } = useTicketDashboardQuery(
    {
      dashboardType: "volume",
      selectedTeam: teamId ?? "",
      selectedWorkFlow: workflowId ?? "",
      graphStartDate: modalRangeStrings.start,
      graphEndDate: modalRangeStrings.end,
    },
    {
      skip:
        !fullscreenChart ||
        !teamId ||
        !modalRangeStrings.start ||
        !modalRangeStrings.end,
    },
  );

  const expandStart = fullscreenChart ? modalRangeStrings.start : startDate ?? "";
  const expandEnd = fullscreenChart ? modalRangeStrings.end : endDate ?? "";

  const volumeResults = useMemo(
    () => extractVolumeResults(intervalData),
    [intervalData],
  );

  const modalEffectiveResults = useMemo(() => {
    if (!fullscreenChart) return volumeResults;
    const modalResults = extractVolumeResults(modalOverviewData);
    return modalResults.length > 0 ? modalResults : volumeResults;
  }, [fullscreenChart, modalOverviewData, volumeResults]);

  const { chartData, dateLabels } = useMemo(
    () => buildVolumeHourlySeries(volumeResults),
    [volumeResults],
  );

  const modalSeries = useMemo(
    () => buildVolumeHourlySeries(modalEffectiveResults),
    [modalEffectiveResults],
  );
  const modalChartData = modalSeries.chartData;

  const volumeDatesFingerprint = useMemo(
    () => volumeResults.map((d) => normalizeDayDateToIsoKey(d.date)).join("|"),
    [volumeResults],
  );

  useEffect(() => {
    setSelectedDates(
      volumeResults.map((day) => normalizeDayDateToIsoKey(day.date)),
    );
  }, [volumeDatesFingerprint, volumeResults]);

  useEffect(() => {
    if (fullscreenChart) {
      setModalSelectedDates(modalSeries.dateLabels);
    }
  }, [fullscreenChart, modalSeries.dateLabels]);

  const totalCount = useMemo(
    () =>
      chartData.reduce(
        (sum, row) =>
          sum +
          selectedDates.reduce((daySum, date) => daySum + (row[date] ?? 0), 0),
        0,
      ),
    [chartData, selectedDates],
  );

  const modalTableData = useMemo(
    () =>
      modalEffectiveResults.map((day) => ({
        date: formatIsoForDayLabel(normalizeDayDateToIsoKey(day.date)),
        ...day.hourly_count,
      })),
    [modalEffectiveResults],
  );

  // ── Click-to-expand ─────────────────────────────────────────────────────────
  const clickToExpandParams =
    tableModalOpen && selectedHour != null && teamId && expandStart && expandEnd
      ? {
          graph_name: "ticket_volume_by_hour",
          value: selectedHour.padStart(2, "0"),
          start_date: expandStart,
          end_date: expandEnd,
          team_id: teamId,
          page: clickToExpandPage,
          page_size: 10,
          workflow_id: workflowId || null,
        }
      : null;

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } =
    useGetTicketClickToExpandQuery(
      clickToExpandParams ?? {
        graph_name: "",
        value: "",
        start_date: "",
        end_date: "",
        team_id: "",
      },
      { skip: !clickToExpandParams },
    );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw =
      (clickToExpandData as any)?.results ??
      (clickToExpandData as any)?.data ??
      clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);

  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page = d.page ?? clickToExpandPage;
    const pageSize = d.page_size ?? 10;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    if (tableModalOpen) setClickToExpandPage(1);
  }, [selectedHour]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const hourValueFromData = (data: any) => {
    if (data?.rawHour != null && data.rawHour !== "") {
      const num = Number(data.rawHour);
      if (!Number.isNaN(num) && num >= 0 && num <= 23) {
        return String(num).padStart(2, "0");
      }
    }
    if (!data?.hour) return null;
    const num = parseInt(String(data.hour).split(":")[0], 10);
    if (Number.isNaN(num) || num < 0 || num > 23) return null;
    return String(num).padStart(2, "0");
  };

  const handlePointClick = useCallback(
    (data: any) => {
      const hourVal = hourValueFromData(data);
      if (!teamId || !expandStart || !expandEnd || hourVal == null) return;
      setSelectedHour(hourVal);
      setTableModalOpen(true);
      setClickToExpandPage(1);
    },
    [teamId, expandStart, expandEnd],
  );

  const handleChartClick = () => {
    const data = hoveredPointRef.current;
    if (data) {
      const hourVal = hourValueFromData(data);
      if (hourVal != null && teamId && expandStart && expandEnd)
        handlePointClick(data);
    }
  };

  const toggleDateVisibility = (date: string) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date],
    );
  };

  const toggleModalDateVisibility = useCallback((date: string) => {
    setModalSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date],
    );
  }, []);

  const hourlyLegendBindRef = useRef({
    selDates: [] as string[],
    onToggle: (_date: string) => {},
    fullscreen: false,
  });

  const hourlyTooltipConfigRef = useRef<HourlyTooltipConfig>({
    selDates: [],
    exS: "",
    exE: "",
  });

  const hourlyTooltipContent = useMemo(
    () => (
      <HourlyDaySeriesTooltip
        configRef={hourlyTooltipConfigRef}
        teamId={teamId}
        hoveredPointRef={hoveredPointRef}
        entity="ticket"
      />
    ),
    [teamId],
  );

  const hourlyLegendRender = useCallback(
    (props: any) => (
      <HourlyVolumeLegend
        payload={props?.payload}
        selDates={hourlyLegendBindRef.current.selDates}
        onToggle={hourlyLegendBindRef.current.onToggle}
        fullscreen={hourlyLegendBindRef.current.fullscreen}
      />
    ),
    [],
  );

  // ── Recharts "as any" aliases (mirrors Inbox pattern) ──────────────────────
  const ResponsiveChartContainer = ResponsiveContainer as any;
  const AreaChartContainer = AreaChart as any;
  const AreaContainer = Area as any;
  const XAxisContainer = XAxis as any;
  const YAxisContainer = YAxis as any;
  const TooltipContainer = Tooltip as any;
  const BarChartContainer = BarChart as any;
  const BarContainer = Bar as any;
  const LineChartContainer = LineChart as any;
  const LineContainer = Line as any;
  const CartesianGridContainer = CartesianGrid as any;
  const LegendContainer = Legend as any;

  // ── Shared axis / grid props ────────────────────────────────────────────────
  const commonAxisProps = {
    xAxis: {
      dataKey: "hour",
      tick: { fontSize: 12, fill: "#64748b", fontWeight: 500 },
      tickLine: false,
      axisLine: { stroke: "#cbd5e1", strokeWidth: 2 },
      label: {
        value: "Hour of day",
        position: "insideBottom",
        offset: -5,
        style: { fontSize: 13, fill: "#475569", fontWeight: 600 },
      },
    },
    yAxis: {
      tick: { fontSize: 12, fill: "#64748b", fontWeight: 500 },
      tickLine: false,
      axisLine: { stroke: "#cbd5e1", strokeWidth: 2 },
      tickFormatter: (value: number) => Math.floor(value).toString(),
      label: {
        value: "Ticket Count",
        angle: -90,
        position: "insideLeft",
        style: { fontSize: 13, fill: "#475569", fontWeight: 600 },
      },
      allowDecimals: false,
      tickCount: 4,
      domain: [
        0,
        (dataMax: number) => {
          if (!dataMax || dataMax === 0) return 3;
          return Math.ceil(dataMax / 2) * 2;
        },
      ],
    },
  };

  const gridProps = {
    strokeDasharray: "3 3",
    stroke: "#e2e8f0",
    vertical: false,
  };

  // ── Render chart ────────────────────────────────────────────────────────────
  const renderChart = (
    data: any[],
    dates: string[],
    visibleDates: string[],
    height: any = "100%",
    fullscreen = false,
  ) => {
    hourlyLegendBindRef.current = {
      selDates: visibleDates,
      onToggle: fullscreen ? toggleModalDateVisibility : toggleDateVisibility,
      fullscreen,
    };

    const getDateColor = (date: string) => {
      const idx = dates.indexOf(date);
      return VOLUME_COLOR_PALETTE[
        idx >= 0 ? idx % VOLUME_COLOR_PALETTE.length : 0
      ];
    };

    hourlyTooltipConfigRef.current = {
      selDates: visibleDates,
      exS: expandStart,
      exE: expandEnd,
      chartRootRef: fullscreen ? modalChartRef : mainChartRef,
      dateColors: Object.fromEntries(
        dates.map((date) => [date, getDateColor(date)]),
      ),
    };

    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
    };

    if (chartType === "area") {
      return (
        <ResponsiveChartContainer width="100%" height={height}>
          <AreaChartContainer
            {...commonProps}
          >
            <defs>
              {dates.map((date, index) => (
                <linearGradient
                  key={date}
                  id={`gradient-ticket-${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={getDateColor(date)}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={getDateColor(date)}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGridContainer {...gridProps} />
            <XAxisContainer {...commonAxisProps.xAxis} />
            <YAxisContainer {...commonAxisProps.yAxis} />
            <LegendContainer {...hourlyChartLegendProps} content={hourlyLegendRender} />
            <TooltipContainer
              {...hourlyChartTooltipProps}
              content={hourlyTooltipContent}
            />
            {dates.map((date, index) => (
              <AreaContainer
                key={date}
                type="monotone"
                dataKey={date}
                name={formatIsoForDayLabel(date)}
                stroke={getDateColor(date)}
                strokeWidth={3}
                fill={`url(#gradient-ticket-${index})`}
                fillOpacity={visibleDates.includes(date) ? 1 : 0}
                hide={!visibleDates.includes(date)}
                animationDuration={1000}
              />
            ))}
          </AreaChartContainer>
        </ResponsiveChartContainer>
      );
    } else if (chartType === "bar") {
      const CellContainer = Cell as any;
      return (
        <ResponsiveChartContainer width="100%" height={height}>
          <BarChartContainer
            {...commonProps}
          >
            <CartesianGridContainer {...gridProps} />
            <XAxisContainer {...commonAxisProps.xAxis} />
            <YAxisContainer {...commonAxisProps.yAxis} />
            <LegendContainer {...hourlyChartLegendProps} content={hourlyLegendRender} />
            <TooltipContainer
              {...hourlyChartTooltipProps}
              content={hourlyTooltipContent}
            />
            {dates.map((date) => (
              <BarContainer
                key={date}
                dataKey={date}
                name={formatIsoForDayLabel(date)}
                fill={getDateColor(date)}
                radius={[8, 8, 0, 0]}
                hide={!visibleDates.includes(date)}
                animationDuration={1000}
                cursor={teamId && expandStart && expandEnd ? "pointer" : "default"}
              >
                <CellContainer
                  cursor={
                    teamId && expandStart && expandEnd ? "pointer" : "default"
                  }
                />
              </BarContainer>
            ))}
          </BarChartContainer>
        </ResponsiveChartContainer>
      );
    } else {
      return (
        <ResponsiveChartContainer width="100%" height={height}>
          <LineChartContainer
            {...commonProps}
          >
            <CartesianGridContainer {...gridProps} />
            <XAxisContainer {...commonAxisProps.xAxis} />
            <YAxisContainer {...commonAxisProps.yAxis} />
            <LegendContainer {...hourlyChartLegendProps} content={hourlyLegendRender} />
            <TooltipContainer
              {...hourlyChartTooltipProps}
              content={hourlyTooltipContent}
            />
            {dates.map((date) => (
              <LineContainer
                key={date}
                type="monotone"
                dataKey={date}
                name={formatIsoForDayLabel(date)}
                stroke={getDateColor(date)}
                strokeWidth={visibleDates.includes(date) ? 3 : 0}
                dot={false}
                activeDot={{
                  r: 7,
                  strokeWidth: 3,
                  stroke: "#fff",
                  fill: getDateColor(date),
                }}
                hide={!visibleDates.includes(date)}
                animationDuration={1000}
              />
            ))}
          </LineChartContainer>
        </ResponsiveChartContainer>
      );
    }
  };

  // ── Fullscreen Modal (identical structure to Inbox) ─────────────────────────
  const FullscreenModal = ({
    isOpen,
    onClose,
    title,
  }: {
    isOpen: boolean;
    onClose: () => void;
    title: string;
  }) => {
    if (!isOpen) return null;

    const tableKeys =
      modalTableData && modalTableData.length > 0
        ? Object.keys(modalTableData[0])
        : [];

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.75)",
          zIndex: 9999,
          padding: "24px",
          overflow: "auto",
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "20px",
            width: "95%",
            maxWidth: "1600px",
            margin: "0 auto",
            padding: "32px",
            position: "relative",
            boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "28px",
              paddingBottom: "20px",
              borderBottom: "2px solid #e2e8f0",
            }}
          >
            <div>
              <h2 style={{ margin: 0, color: "#1f2937", fontWeight: 700 }}>
                {title}
              </h2>
              <p
                style={{
                  margin: "8px 0 0 0",
                  color: "#6b7280",
                  fontSize: "14px",
                }}
              >
                Interactive ticket traffic analysis
              </p>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
                flexShrink: 0,
              }}
            >
              <div style={{ position: "relative", zIndex: 10002 }} onClick={(e) => e.stopPropagation()}>
                <CustomDate
                  dateRange={modalDateRange}
                  setDateRange={setModalDateRange}
                  unrestricted
                />
              </div>
              <button
                onClick={onClose}
                style={{
                  background:
                    "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "44px",
                  height: "44px",
                  fontSize: "24px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s",
                  boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "rotate(90deg) scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "rotate(0deg) scale(1)";
                }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Chart */}
          <div style={{ marginBottom: "32px" }}>
            <div
              ref={modalChartRef}
              style={{
                width: "100%",
                height: "550px",
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                overflow: "visible",
                position: "relative",
                cursor: teamId && expandStart && expandEnd ? "pointer" : "default",
              }}
              onClick={handleChartClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleChartClick();
              }}
            >
              {renderChart(
                modalChartData,
                modalSeries.dateLabels,
                modalSelectedDates,
                "100%",
                true,
              )}
            </div>
          </div>

          {/* Data table */}
          {modalTableData && modalTableData.length > 0 && (
            <div style={{ marginTop: "32px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "#1f2937",
                    fontSize: "1.5em",
                    fontWeight: 700,
                  }}
                >
                  📊 Underlying data
                </h3>
                <span
                  style={{
                    backgroundColor: "#6366F1",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {modalTableData.length} rows
                </span>
              </div>

              <div
                style={{
                  overflowX: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  maxHeight: "450px",
                  overflowY: "auto",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "14px",
                  }}
                >
                  <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr style={{ backgroundColor: "#f8fafc" }}>
                      {tableKeys.map((key) => (
                        <th
                          key={key}
                          style={{
                            padding: "14px 18px",
                            textAlign: "left",
                            borderBottom: "2px solid #cbd5e1",
                            fontWeight: 700,
                            color: "#4b5563",
                            whiteSpace: "nowrap",
                            position: "sticky",
                            top: 0,
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          {key.replace(/_/g, " ").toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modalTableData.map((row, index) => (
                      <tr
                        key={index}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#ffffff" : "#f8fafc",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#eef2ff")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            index % 2 === 0 ? "#ffffff" : "#f8fafc")
                        }
                      >
                        {tableKeys.map((key) => (
                          <td
                            key={key}
                            style={{
                              padding: "14px 18px",
                              borderBottom: "1px solid #e2e8f0",
                              color: "#4b5563",
                            }}
                          >
                            {row[key] !== null && row[key] !== undefined
                              ? row[key] === 0
                                ? "-"
                                : String(row[key])
                              : "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Styles (mirrors Inbox) ──────────────────────────────────────────────────
  const styles = {
    chartTypeButton: {
      padding: "8px 16px",
      border: "2px solid #e2e8f0",
      borderRadius: "8px",
      backgroundColor: "#ffffff",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: 600,
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    } as React.CSSProperties,
    expandButton: {
      background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      padding: "12px 20px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 600,
      transition: "all 0.3s",
      boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    } as React.CSSProperties,
  };

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        overflow: "visible",
      }}
    >
      <div style={{ padding: "32px" }}>
        {/* ── Header row (identical to Inbox) ─────────────────────────────── */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontWeight: 700,
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              {/* Ticket icon */}
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6366f1"
                strokeWidth="2"
              >
                <line x1="12" y1="20" x2="12" y2="10" />
                <line x1="18" y1="20" x2="18" y2="4" />
                <line x1="6" y1="20" x2="6" y2="16" />
              </svg>
              {`Ticket volume by hour: ${totalCount.toLocaleString()}`}
            </h2>
            <p
              style={{
                margin: "8px 0 0 0",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              Hourly ticket count by day
            </p>
          </div>

          {/* Controls */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* Chart-type switcher */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                backgroundColor: "#f8fafc",
                padding: "4px",
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
              }}
            >
              {(["bar", "area", "line"] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setChartType(type)}
                  style={{
                    ...styles.chartTypeButton,
                    backgroundColor:
                      chartType === type ? "#ffffff" : "transparent",
                    border:
                      chartType === type
                        ? "2px solid #6366f1"
                        : "2px solid transparent",
                    color: chartType === type ? "#6366f1" : "#64748b",
                    boxShadow:
                      chartType === type
                        ? "0 2px 8px rgba(99, 102, 241, 0.2)"
                        : "none",
                  }}
                >
                  {type === "line" && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                  )}
                  {type === "area" && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M3 3v18h18" />
                      <path d="M18 17V9l-5 5-5-5v8" />
                    </svg>
                  )}
                  {type === "bar" && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="12" y1="20" x2="12" y2="10" />
                      <line x1="18" y1="20" x2="18" y2="4" />
                      <line x1="6" y1="20" x2="6" y2="16" />
                    </svg>
                  )}
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            {/* Expand View */}
            <button
              onClick={() =>
                setFullscreenChart({
                  title: "Ticket volume by hour",
                })
              }
              style={styles.expandButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(99, 102, 241, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(99, 102, 241, 0.3)";
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
              </svg>
              Expand View
            </button>
          </div>
        </div>

        {/* ── Chart area (identical wrapper to Inbox) ──────────────────────── */}
        <div
          ref={mainChartRef}
          style={{
            width: "100%",
            height: "600px",
            backgroundColor: "#fafbfc",
            padding: "24px",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            overflow: "visible",
            position: "relative",
            cursor: teamId && expandStart && expandEnd ? "pointer" : "default",
          }}
          onClick={handleChartClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleChartClick();
          }}
        >
          {renderChart(chartData, dateLabels, selectedDates, "100%")}
        </div>
      </div>

      {/* ── Click-to-expand TableModal ───────────────────────────────────── */}
      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={() => {
          setTableModalOpen(false);
          setSelectedHour(null);
          setClickToExpandPage(1);
        }}
        title={
          selectedHour != null
            ? `Ticket volume – Hour ${selectedHour}:00`
            : "Tickets"
        }
        contentLabel="Ticket volume by hour click-to-expand"
        emptyMessage="No tickets in this segment."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx: number) => row?.ticket_id ?? row?.id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const ticketId = row?.ticket_id ?? row?.id;
          if (ticketId != null) {
            setTableModalOpen(false);
            setSelectedHour(null);
            navigate(`/ticket/information/${ticketId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={TICKET_TABLE_COLUMNS}
      />

      {/* ── Fullscreen Modal ─────────────────────────────────────────────── */}
      <FullscreenModal
        key={fullscreenChart?.title}
        isOpen={fullscreenChart !== null}
        onClose={() => setFullscreenChart(null)}
        title={fullscreenChart?.title || ""}
      />
    </div>
  );
};

export default TicketVolumeChart;
